import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { useConfig } from '../context/ConfigContext';

function BusinessSettings() {
  const navigate = useNavigate();
  const { isDemoMode } = useConfig();
  const demoDisabled = isDemoMode();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Contract Fees
  const [contractFees, setContractFees] = useState({
    processingFee: 99,
    lateFeeAmount: 75,
    gracePeriodDays: 7,
    noticeFeeAmount: 75,
    returnedPaymentFee: 75,
    changeDueDateFee: 75,
    propertyTransferFee: 175,
    lienReleaseFee: 75,
    curePeriodDays: 14,
    // Matches DEFAULT_NOTICE_ELIGIBILITY_DAYS in server.js, which resolves from
    // siteConfig.BUSINESS_SETTINGS.noticeEligibilityDays. Only ever shown if the
    // GET fails, but without it the input mounts uncontrolled on that path.
    noticeEligibilityDays: 30,
    // Mirror DEFAULT_ESCROW_ALERT_DAYS and DEFAULT_ESCROW_REPRICE_MIN_MONTHS in
    // server/lib/settings.js. Only reached if the GET fails; without them the
    // inputs would mount uncontrolled on that path.
    escrowAlertDays: 30,
    escrowRepriceMinMonths: 12
  });

  // Payment Processing Fees
  const [processingFees, setProcessingFees] = useState({
    convenienceFee: 5,
    ccProcessingRate: 2.9,
    ccPerTransactionFee: 0.30
  });

  // Financing Options
  //
  // Starts EMPTY. This form used to open pre-filled with one specific business's
  // tiers ($99 down at 18% APR, 20% at 12%, 50% at 8%), so a new tenant who hit
  // Save without touching this section adopted a stranger's prices as their own
  // and had no way to tell they had done it. Every other field on this page has
  // a defensible platform default; a down payment and an APR do not.
  //
  // An empty list is also what the server now resolves to for a tenant with no
  // saved rows, so the form shows the true state instead of a suggestion.
  const [financingTiers, setFinancingTiers] = useState([]);
  const [termOptions, setTermOptions] = useState([1, 2, 3, 4, 5]);
  const [minimumMonthlyPayment, setMinimumMonthlyPayment] = useState(50);

  // Payment due day.
  //
  // Both keys have always existed in storage and both have always been read by
  // the loan routes; neither had an input on this page, so paymentDueDayOptions
  // could only ever be changed by editing site_settings by hand. That is the same
  // shape of gap noticeEligibilityDays had -- returned by the GET, held in memory,
  // posted back, and never editable.
  //
  // Mirrors siteConfig.BUSINESS_SETTINGS on the server. Only reached if the GET
  // fails; without them the controls would mount uncontrolled on that path.
  const [paymentDueDayMode, setPaymentDueDayMode] = useState('offered');
  const [paymentDueDayOptions, setPaymentDueDayOptions] = useState([1, 15]);

  // Property Limits
  const [propertyLimits, setPropertyLimits] = useState({
    minPropertyPrice: 2000,
    maxPropertyPrice: 50000
  });

  useEffect(() => {
    fetchBusinessSettings();
  }, []);

  const fetchBusinessSettings = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await api.get('/admin/business-settings', {
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = response.data;

      if (data.contractFees) setContractFees(data.contractFees);
      if (data.processingFees) setProcessingFees(data.processingFees);
      if (data.financingTiers) setFinancingTiers(data.financingTiers);
      if (data.termOptions) setTermOptions(data.termOptions);
      if (data.paymentDueDayMode) setPaymentDueDayMode(data.paymentDueDayMode);
      // Array.isArray and not a truthiness test: [] is a real saved state — a
      // tenant that has cleared every day — and `if (data.paymentDueDayOptions)`
      // would accept it, but the same test on any other array-valued key here
      // would not. Kept explicit so the empty case is visibly intentional.
      if (Array.isArray(data.paymentDueDayOptions)) setPaymentDueDayOptions(data.paymentDueDayOptions);
      if (data.minimumMonthlyPayment) setMinimumMonthlyPayment(data.minimumMonthlyPayment);
      if (data.propertyLimits) setPropertyLimits(data.propertyLimits);

      setError('');
    } catch (err) {
      console.error('Failed to fetch business settings:', err);
      // Use defaults if fetch fails (first time setup)
      if (err.response?.status !== 404) {
        setError('Failed to load business settings');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('adminToken');
      await api.post('/admin/business-settings', {
        contractFees,
        processingFees,
        financingTiers,
        termOptions,
        paymentDueDayMode,
        paymentDueDayOptions,
        minimumMonthlyPayment,
        propertyLimits
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSuccess('Business settings saved successfully!');
      window.scrollTo(0, 0);
    } catch (err) {
      console.error('Failed to save business settings:', err);
      setError(err.response?.data?.error || 'Failed to save business settings');
    } finally {
      setSaving(false);
    }
  };

  const handleContractFeeChange = (key, value) => {
    setContractFees(prev => ({ ...prev, [key]: parseFloat(value) || 0 }));
  };

  const handleProcessingFeeChange = (key, value) => {
    setProcessingFees(prev => ({ ...prev, [key]: parseFloat(value) || 0 }));
  };

  const handlePropertyLimitChange = (key, value) => {
    setPropertyLimits(prev => ({ ...prev, [key]: parseFloat(value) || 0 }));
  };

  // [...financingTiers] is a SHALLOW copy, so the previous version mutated the
  // very tier objects still held in state before calling the setter. That was
  // not the cause of the vanishing MOST POPULAR badge -- that was a missing
  // render branch in PropertyDetail -- but mutating state in place defeats any
  // reference comparison React or a memo does, so the row is replaced instead.
  const handleTierChange = (index, key, value) => {
    const numericKey = key === 'downPayment' || key === 'downPaymentPercent' || key === 'apr';
    const nextValue = numericKey ? (value === '' ? null : parseFloat(value)) : value;

    // isPopular is exclusive: checking one tier clears every other. The control
    // is a checkbox rather than a radio because unchecking must be allowed --
    // "no tier is popular" is a valid configuration and is NOT coerced back into
    // a selection. Ticking a second tier without this would leave the property
    // page showing several MOST POPULAR badges at once.
    if (key === 'isPopular') {
      setFinancingTiers(prev => prev.map((tier, i) => ({
        ...tier,
        isPopular: value === true && i === index
      })));
      return;
    }

    setFinancingTiers(prev => prev.map((tier, i) =>
      i === index ? { ...tier, [key]: nextValue } : tier
    ));
  };

  const addTier = () => {
    setFinancingTiers([...financingTiers, {
      name: "New Tier",
      downPayment: null,
      downPaymentPercent: 10,
      apr: 15,
      isPopular: false
    }]);
  };

  const removeTier = (index) => {
    if (financingTiers.length > 1) {
      setFinancingTiers(financingTiers.filter((_, i) => i !== index));
    }
  };

  const handleTermToggle = (year) => {
    if (termOptions.includes(year)) {
      setTermOptions(termOptions.filter(t => t !== year));
    } else {
      setTermOptions([...termOptions, year].sort((a, b) => a - b));
    }
  };

  // Sorted numerically on every toggle, so the order a tenant happens to click
  // in never reaches storage. formatDueDayOptions() renders this list verbatim
  // into buyer-facing prose ("1st, 10th or 20th"), and the purchase page renders
  // one tile per entry in list order.
  const handleDueDayToggle = (day) => {
    if (paymentDueDayOptions.includes(day)) {
      setPaymentDueDayOptions(paymentDueDayOptions.filter(d => d !== day));
    } else {
      setPaymentDueDayOptions([...paymentDueDayOptions, day].sort((a, b) => a - b));
    }
  };

  if (loading) {
    return (
      <div className="admin-dashboard-container">
        <div className="admin-dashboard-header">
          <div className="admin-dashboard-header-text">
            <h1 className="admin-page-title">Loading...</h1>
          </div>
        </div>
      </div>
    );
  }

  const inputStyle = {
    padding: '10px 12px',
    border: '1px solid var(--color-border)',
    borderRadius: '6px',
    // 16px, not 14px: below 16 iOS zooms the viewport on focus. minHeight keeps
    // the control on the 44px touch target the shared .form-input rule uses.
    fontSize: '16px',
    minHeight: '44px',
    boxSizing: 'border-box',
    width: '120px'
  };

  const labelStyle = {
    display: 'block',
    marginBottom: '5px',
    fontWeight: '600',
    color: 'var(--color-text)'
  };

  const descStyle = {
    fontSize: '12px',
    color: 'var(--color-text-light)',
    margin: '0 0 8px 0'
  };

  const sectionHeaderStyle = {
    marginTop: 0,
    marginBottom: '10px',
    color: 'var(--color-primary)',
    fontSize: '18px'
  };

  const sectionSubtitleStyle = {
    fontSize: '13px',
    color: 'var(--color-text-light)',
    marginBottom: '20px',
    fontStyle: 'italic'
  };

  const fieldRowStyle = {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '20px',
    marginBottom: '15px'
  };

  return (
    <div className="admin-dashboard-container">
      {/* Header */}
      <div className="admin-hub-header">
        <button onClick={() => navigate('/admin/settings-hub')} className="admin-hub-back-btn">
          <span className="admin-hub-back-arrow">←</span>
          Back to Settings
        </button>
        <h1 className="admin-page-title">Business Settings</h1>
        <p className="admin-hub-subtitle">Configure fees, financing options, and business rules</p>
      </div>

      {/* Note */}
      <div style={{
        backgroundColor: 'var(--color-warning-bg)',
        border: '1px solid var(--color-warning-border)',
        borderRadius: '8px',
        padding: '15px',
        marginBottom: '20px',
        fontSize: '14px',
        color: 'var(--color-warning-text)'
      }}>
        <strong>Note:</strong> Leave any fee at $0 to exclude it from contracts and customer-facing pages.
      </div>

      {/* Alert Messages */}
      {error && (
        <div className="alert alert-danger" style={{ marginBottom: '20px' }}>
          {error}
        </div>
      )}
      {success && (
        <div style={{
          backgroundColor: 'var(--color-success-bg)',
          border: '1px solid var(--color-success-border)',
          color: 'var(--color-success-text)',
          padding: '12px 16px',
          borderRadius: '6px',
          marginBottom: '20px',
          fontWeight: '500',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <span style={{ fontSize: '18px' }}>✓</span> {success}
        </div>
      )}

      {/* Section 1: Contract Fees */}
      <div className="card" style={{ padding: '25px', marginBottom: '20px' }}>
        <h2 style={sectionHeaderStyle}>Contract Fees</h2>
        <p style={sectionSubtitleStyle}>
          These fees appear in Section 26 "Other Provisions" of the land contract
        </p>

        <div style={fieldRowStyle}>
          <div>
            <label style={labelStyle}>Contract/Document Fee</label>
            <p style={descStyle}>One-time fee at closing</p>
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              <span>$</span>
              <input
                type="number"
                value={contractFees.processingFee}
                onChange={(e) => handleContractFeeChange('processingFee', e.target.value)}
                style={inputStyle}
                min="0"
                step="1"
              />
            </div>
          </div>
          <div>
            <label style={labelStyle}>Late Payment Fee</label>
            <p style={descStyle}>Charged after grace period expires</p>
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              <span>$</span>
              <input
                type="number"
                value={contractFees.lateFeeAmount}
                onChange={(e) => handleContractFeeChange('lateFeeAmount', e.target.value)}
                style={inputStyle}
                min="0"
                step="1"
              />
            </div>
          </div>
        </div>

        <div style={fieldRowStyle}>
          <div>
            <label style={labelStyle}>Grace Period (days)</label>
            <p style={descStyle}>Days after due date before late fee applies</p>
            <input
              type="number"
              value={contractFees.gracePeriodDays}
              onChange={(e) => handleContractFeeChange('gracePeriodDays', e.target.value)}
              style={inputStyle}
              min="0"
              step="1"
            />
          </div>
          <div>
            <label style={labelStyle}>Default/Cure Notice Fee</label>
            <p style={descStyle}>Fee for sending cure/default notice</p>
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              <span>$</span>
              <input
                type="number"
                value={contractFees.noticeFeeAmount}
                onChange={(e) => handleContractFeeChange('noticeFeeAmount', e.target.value)}
                style={inputStyle}
                min="0"
                step="1"
              />
            </div>
          </div>
        </div>

        <div style={fieldRowStyle}>
          <div>
            <label style={labelStyle}>Returned Payment Fee (NSF/ACH)</label>
            <p style={descStyle}>Fee for bounced checks or failed ACH</p>
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              <span>$</span>
              <input
                type="number"
                value={contractFees.returnedPaymentFee}
                onChange={(e) => handleContractFeeChange('returnedPaymentFee', e.target.value)}
                style={inputStyle}
                min="0"
                step="1"
              />
            </div>
          </div>
          <div>
            <label style={labelStyle}>Change Due Date Fee</label>
            <p style={descStyle}>Fee to change monthly payment date</p>
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              <span>$</span>
              <input
                type="number"
                value={contractFees.changeDueDateFee}
                onChange={(e) => handleContractFeeChange('changeDueDateFee', e.target.value)}
                style={inputStyle}
                min="0"
                step="1"
              />
            </div>
          </div>
        </div>

        <div style={fieldRowStyle}>
          <div>
            <label style={labelStyle}>Property Transfer Fee</label>
            <p style={descStyle}>Fee to transfer contract to new buyer</p>
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              <span>$</span>
              <input
                type="number"
                value={contractFees.propertyTransferFee}
                onChange={(e) => handleContractFeeChange('propertyTransferFee', e.target.value)}
                style={inputStyle}
                min="0"
                step="1"
              />
            </div>
          </div>
          <div>
            <label style={labelStyle}>Lien Release Processing Fee</label>
            <p style={descStyle}>Fee to process lien release at payoff</p>
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              <span>$</span>
              <input
                type="number"
                value={contractFees.lienReleaseFee}
                onChange={(e) => handleContractFeeChange('lienReleaseFee', e.target.value)}
                style={inputStyle}
                min="0"
                step="1"
              />
            </div>
          </div>
        </div>

        {/* The two day counts Section 9 of the contract renders. Both are frozen
            onto the loan at origination (migration 040), so both carry the same
            warning: an admin editing them is setting the terms of the NEXT loan,
            not of any loan already signed.

            Own grid class rather than fieldRowStyle's fixed `1fr 1fr`, so the
            pair collapses to one column on a phone instead of becoming two
            narrow ones -- see .business-settings-period-grid in index.css. */}
        <div className="business-settings-period-grid">
          <div>
            <label style={labelStyle}>Cure Period (days after notice)</label>
            <p className="business-settings-period-help">
              Days the buyer has to cure a default after a notice is sent. Stated in
              Section 9 of the contract. Governs NEW loans only. Existing loans keep
              the number their signed contract states.
            </p>
            <input
              type="number"
              className="business-settings-period-input"
              value={contractFees.curePeriodDays}
              onChange={(e) => handleContractFeeChange('curePeriodDays', e.target.value)}
              min="0"
              step="1"
            />
          </div>
          <div>
            <label style={labelStyle}>Notice Eligibility (days past due)</label>
            <p className="business-settings-period-help">
              Days a payment must be past due before a Default and Cure Notice may be
              sent. Stated in Section 9 of the contract. Governs NEW loans only, and
              existing loans keep the number their signed contract states.
            </p>
            <input
              type="number"
              className="business-settings-period-input"
              value={contractFees.noticeEligibilityDays}
              onChange={(e) => handleContractFeeChange('noticeEligibilityDays', e.target.value)}
              min="0"
              step="1"
            />
          </div>
          <div>
            <label style={labelStyle}>Escrow Alert Window (days)</label>
            <p className="business-settings-period-help">
              How far ahead the daily escrow digest looks for a tax installment with
              nothing remitted against it. Past due installments are always reported,
              whatever this is set to.
            </p>
            <input
              type="number"
              className="business-settings-period-input"
              value={contractFees.escrowAlertDays}
              onChange={(e) => handleContractFeeChange('escrowAlertDays', e.target.value)}
              min="0"
              step="1"
            />
          </div>
          <div>
            <label style={labelStyle}>Escrow Re-price Interval (months)</label>
            <p className="business-settings-period-help">
              The shortest time allowed between two escrow changes on one loan.
              Section 8(b) of the contract states this, so raising it does not
              re-open a loan that has already been adjusted inside the old window.
            </p>
            <input
              type="number"
              className="business-settings-period-input"
              value={contractFees.escrowRepriceMinMonths}
              onChange={(e) => handleContractFeeChange('escrowRepriceMinMonths', e.target.value)}
              min="1"
              step="1"
            />
          </div>
        </div>
      </div>

      {/* Section 2: Payment Processing Fees */}
      <div className="card" style={{ padding: '25px', marginBottom: '20px' }}>
        <h2 style={sectionHeaderStyle}>Payment Processing Fees</h2>
        <p style={sectionSubtitleStyle}>
          Displayed on payment pages and receipts
        </p>

        <div style={fieldRowStyle}>
          <div>
            <label style={labelStyle}>Online Payment Convenience Fee</label>
            <p style={descStyle}>Added to each online payment</p>
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              <span>$</span>
              <input
                type="number"
                value={processingFees.convenienceFee}
                onChange={(e) => handleProcessingFeeChange('convenienceFee', e.target.value)}
                style={inputStyle}
                min="0"
                step="0.01"
              />
            </div>
          </div>
          <div>
            <label style={labelStyle}>Credit Card Processing Rate (%)</label>
            <p style={descStyle}>Your processor's percentage rate</p>
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              <input
                type="number"
                value={processingFees.ccProcessingRate}
                onChange={(e) => handleProcessingFeeChange('ccProcessingRate', e.target.value)}
                style={inputStyle}
                min="0"
                step="0.1"
              />
              <span>%</span>
            </div>
          </div>
        </div>

        <div style={{ ...fieldRowStyle, gridTemplateColumns: '1fr' }}>
          <div>
            <label style={labelStyle}>Credit Card Per-Transaction Fee</label>
            <p style={descStyle}>Fixed fee per transaction (e.g., $0.30)</p>
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              <span>$</span>
              <input
                type="number"
                value={processingFees.ccPerTransactionFee}
                onChange={(e) => handleProcessingFeeChange('ccPerTransactionFee', e.target.value)}
                style={inputStyle}
                min="0"
                step="0.01"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Section 3: Financing Options */}
      <div className="card" style={{ padding: '25px', marginBottom: '20px' }}>
        <h2 style={sectionHeaderStyle}>Financing Options</h2>
        <p style={sectionSubtitleStyle}>
          Shown on property detail pages and used for loan calculations
        </p>

        <h3 style={{ fontSize: '16px', marginBottom: '15px', color: 'var(--color-text)' }}>Financing Tiers</h3>

        {financingTiers.length === 0 && (
          <div style={{
            border: '1px dashed var(--color-warning-border)',
            borderRadius: '8px',
            padding: '20px',
            marginBottom: '15px',
            backgroundColor: 'var(--color-warning-bg)'
          }}>
            <strong style={{ display: 'block', marginBottom: '6px', color: 'var(--color-warning-text)' }}>
              No financing options yet
            </strong>
            <p style={{ margin: 0, fontSize: '14px', color: 'var(--color-warning-text)', lineHeight: 1.5 }}>
              These are your terms: the down payment and interest rate a buyer is quoted and
              signs for. There is no default, because they are yours to set. Add at least one
              tier below before you publish a property; until then listings can be saved as
              drafts but cannot be set to Available.
            </p>
          </div>
        )}

        {financingTiers.map((tier, index) => (
          <div key={index} style={{
            border: '1px solid var(--color-border)',
            borderRadius: '8px',
            padding: '15px',
            marginBottom: '15px',
            backgroundColor: tier.isPopular ? '#f0fdf4' : '#fff'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <strong>Tier {index + 1}</strong>
              {financingTiers.length > 1 && (
                <button
                  onClick={() => removeTier(index)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--color-error)',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Remove
                </button>
              )}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '15px', alignItems: 'end' }}>
              <div>
                <label style={{ ...labelStyle, fontSize: '13px' }}>Tier Name</label>
                <input
                  type="text"
                  value={tier.name}
                  onChange={(e) => handleTierChange(index, 'name', e.target.value)}
                  style={{ ...inputStyle, width: '100%' }}
                />
              </div>
              <div>
                <label style={{ ...labelStyle, fontSize: '13px' }}>Fixed Down ($)</label>
                <input
                  type="number"
                  value={tier.downPayment || ''}
                  onChange={(e) => {
                    handleTierChange(index, 'downPayment', e.target.value);
                    if (e.target.value) handleTierChange(index, 'downPaymentPercent', '');
                  }}
                  placeholder="e.g., 99"
                  style={{ ...inputStyle, width: '100%' }}
                  min="0"
                />
              </div>
              <div>
                <label style={{ ...labelStyle, fontSize: '13px' }}>OR Down (%)</label>
                <input
                  type="number"
                  value={tier.downPaymentPercent || ''}
                  onChange={(e) => {
                    handleTierChange(index, 'downPaymentPercent', e.target.value);
                    if (e.target.value) handleTierChange(index, 'downPayment', '');
                  }}
                  placeholder="e.g., 20"
                  style={{ ...inputStyle, width: '100%' }}
                  min="0"
                  max="100"
                />
              </div>
              <div>
                <label style={{ ...labelStyle, fontSize: '13px' }}>APR (%)</label>
                <input
                  type="number"
                  value={tier.apr}
                  onChange={(e) => handleTierChange(index, 'apr', e.target.value)}
                  style={{ ...inputStyle, width: '100%' }}
                  min="0"
                  step="0.1"
                />
              </div>
            </div>

            <div style={{ marginTop: '10px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={tier.isPopular}
                  onChange={(e) => handleTierChange(index, 'isPopular', e.target.checked)}
                />
                <span style={{ fontSize: '13px' }}>Mark as "Popular" (highlighted on property page)</span>
              </label>
            </div>
          </div>
        ))}

        <button
          onClick={addTier}
          style={{
            backgroundColor: 'var(--color-surface-sunken)',
            border: '1px dashed var(--color-border)',
            borderRadius: '6px',
            padding: '10px 20px',
            cursor: 'pointer',
            width: '100%',
            marginBottom: '25px'
          }}
        >
          + Add Another Tier
        </button>

        <h3 style={{ fontSize: '16px', marginBottom: '15px', color: 'var(--color-text)' }}>Term Options (Years)</h3>
        <div style={{ display: 'flex', gap: '15px', marginBottom: '20px' }}>
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(year => (
            <label key={year} style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={termOptions.includes(year)}
                onChange={() => handleTermToggle(year)}
              />
              <span>{year} yr</span>
            </label>
          ))}
        </div>

        <div>
          <label style={labelStyle}>Minimum Monthly Payment</label>
          <p style={descStyle}>Loan terms will be adjusted to ensure payments meet this minimum</p>
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <span>$</span>
            <input
              type="number"
              value={minimumMonthlyPayment}
              onChange={(e) => setMinimumMonthlyPayment(parseFloat(e.target.value) || 0)}
              style={inputStyle}
              min="0"
              step="1"
            />
          </div>
        </div>

        {/* Payment Due Day.
            Two settings that have always been read by the loan routes and have
            never had an input on this page. Own grid classes rather than the
            page's fixed `1fr 1fr`, so 31 day tiles reflow to whatever the
            viewport has instead of forcing it sideways. */}
        <h3 style={{ fontSize: '16px', margin: '30px 0 8px 0', color: 'var(--color-text)' }}>Payment Due Day</h3>
        <p style={descStyle}>
          Governs NEW loans only. An existing loan keeps the due day it was originated
          with, and changing either setting here never moves an existing buyer's
          payment date. Use the Due Day control on the loan itself for that.
        </p>

        <div className="business-settings-dueday-mode-grid">
          <label
            className={`business-settings-dueday-mode${paymentDueDayMode === 'offered' ? ' is-selected' : ''}`}
          >
            <input
              type="radio"
              name="paymentDueDayMode"
              value="offered"
              checked={paymentDueDayMode === 'offered'}
              onChange={() => setPaymentDueDayMode('offered')}
            />
            <span>
              <strong>Buyer chooses a day</strong>
              <span className="business-settings-dueday-mode-help">
                The buyer picks one of the days you offer below. This is the default.
              </span>
            </span>
          </label>

          <label
            className={`business-settings-dueday-mode${paymentDueDayMode === 'anniversary' ? ' is-selected' : ''}`}
          >
            <input
              type="radio"
              name="paymentDueDayMode"
              value="anniversary"
              checked={paymentDueDayMode === 'anniversary'}
              onChange={() => setPaymentDueDayMode('anniversary')}
            />
            <span>
              <strong>Anniversary of closing</strong>
              <span className="business-settings-dueday-mode-help">
                The due day is the day of the month the loan closes. A buyer closing on
                the 22nd pays on the 22nd. No day is offered and no choice is shown.
              </span>
            </span>
          </label>
        </div>

        <label style={labelStyle}>Days Offered</label>
        {paymentDueDayMode === 'anniversary' ? (
          <p className="business-settings-dueday-note">
            Not used in anniversary mode. Each buyer's due day is derived from their own
            closing date, so this list is not offered to anyone. Switch back to
            <strong> Buyer chooses a day</strong> to use it again. The selection below is
            kept either way.
          </p>
        ) : (
          <p style={descStyle}>
            The days of the month a buyer may pick at purchase. Months shorter than the
            chosen day bill on the last day of that month: a due day of 31 falls on
            28 February.
          </p>
        )}

        <div className="business-settings-dueday-grid" aria-disabled={paymentDueDayMode === 'anniversary'}>
          {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
            <label
              key={day}
              className={`business-settings-dueday-option${paymentDueDayOptions.includes(day) ? ' is-selected' : ''}${paymentDueDayMode === 'anniversary' ? ' is-disabled' : ''}`}
            >
              <input
                type="checkbox"
                checked={paymentDueDayOptions.includes(day)}
                onChange={() => handleDueDayToggle(day)}
                disabled={paymentDueDayMode === 'anniversary'}
              />
              <span>{day}</span>
            </label>
          ))}
        </div>

        {paymentDueDayMode === 'offered' && paymentDueDayOptions.length === 0 && (
          <p className="business-settings-dueday-warning">
            No days are selected. Buyers will have no due day to choose, and every new
            loan falls back to the 1st of the month.
          </p>
        )}
      </div>

      {/* Section 4: Property Limits */}
      <div className="card" style={{ padding: '25px', marginBottom: '20px' }}>
        <h2 style={sectionHeaderStyle}>Property Limits</h2>
        <p style={sectionSubtitleStyle}>
          Controls which properties can be listed
        </p>

        <div style={fieldRowStyle}>
          <div>
            <label style={labelStyle}>Minimum Property Price</label>
            <p style={descStyle}>Properties below this price won't be listed</p>
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              <span>$</span>
              <input
                type="number"
                value={propertyLimits.minPropertyPrice}
                onChange={(e) => handlePropertyLimitChange('minPropertyPrice', e.target.value)}
                style={inputStyle}
                min="0"
                step="100"
              />
            </div>
          </div>
          <div>
            <label style={labelStyle}>Maximum Property Price</label>
            <p style={descStyle}>Properties above this price won't be listed</p>
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              <span>$</span>
              <input
                type="number"
                value={propertyLimits.maxPropertyPrice}
                onChange={(e) => handlePropertyLimitChange('maxPropertyPrice', e.target.value)}
                style={inputStyle}
                min="0"
                step="1000"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
        <button
          onClick={() => !demoDisabled && handleSave()}
          disabled={saving || demoDisabled}
          className="btn btn-primary"
          style={{
            padding: '12px 30px',
            fontSize: '16px',
            opacity: (saving || demoDisabled) ? 0.6 : 1,
            cursor: (saving || demoDisabled) ? 'not-allowed' : 'pointer'
          }}
          title={demoDisabled ? 'Disabled in demo mode' : ''}
        >
          {saving ? 'Saving...' : 'Save All Settings'}
        </button>
        <button
          onClick={() => navigate('/admin/dashboard')}
          className="btn btn-secondary"
          style={{ padding: '12px 30px', fontSize: '16px' }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

export default BusinessSettings;
